import React, { forwardRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

/**
 * Card component for displaying content in a styled container
 * @param {string} className - Additional CSS classes
 * @param {React.ReactNode} children - Card content
 * @param {string} title - Optional card title
 * @param {string} subtitle - Optional card subtitle
 * @param {object} icon - Optional FontAwesome icon object
 * @param {string} variant - Color variant: 'default', 'danger', 'warning', 'success', 'info'
 * @param {string} size - Padding size: 'sm', 'md', 'lg'
 * @param {boolean} flat - Remove box shadow
 * @param {boolean} hoverable - Add hover effect
 * @param {boolean} clickable - Show pointer cursor and click effect
 * @param {function} onClick - Click handler (also enables clickable style)
 * @param {React.ReactNode} headerActions - Optional actions to display in the header
 */
const Card = forwardRef(
  (
    {
      className = "",
      children,
      title,
      subtitle,
      icon,
      variant = "default",
      size = "md",
      flat = false,
      hoverable = false,
      clickable = false,
      onClick,
      headerActions,
      ...rest
    },
    ref,
  ) => {
    const isClickable = clickable || !!onClick;

    const cardClasses = [
      "card",
      `card-${size}`,
      variant !== "default" && `card-${variant}`,
      flat && "card-flat",
      hoverable && "card-hoverable",
      isClickable && "card-clickable",
      className,
    ]
      .filter(Boolean)
      .join(" ");

    const hasHeader = title || icon || headerActions;

    return (
      <div
        ref={ref}
        className={cardClasses}
        onClick={onClick}
        role={isClickable ? "button" : undefined}
        tabIndex={isClickable ? 0 : undefined}
        onKeyDown={
          isClickable
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onClick?.(e);
                }
              }
            : undefined
        }
        {...rest}
      >
        {hasHeader && (
          <div className="card-header">
            <div className="card-header-content">
              {icon && (
                <div className="card-icon">
                  <FontAwesomeIcon icon={icon} />
                </div>
              )}
              {(title || subtitle) && (
                <div className="card-titles">
                  {title && <h4 className="card-title">{title}</h4>}
                  {subtitle && <p className="card-subtitle">{subtitle}</p>}
                </div>
              )}
            </div>
            {headerActions && (
              <div className="card-header-actions">{headerActions}</div>
            )}
          </div>
        )}
        {children && (
          <div className={hasHeader ? "card-body" : ""}>{children}</div>
        )}
      </div>
    );
  },
);

Card.displayName = "Card";

export default Card;
